import { DefaultError, QueryClient, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { corporateCMSApi } from "~/Services";
import { PageHeading } from "../MediaCentre/Media.hook";

interface ContentData {
  image: string;
  text: string;
  nextPage: {
    name: string;
    link: string;
  };
}

interface ManagementIntegrity {
  id: number;
  field_content_english: {
    data: ContentData;
  };
  field_content_malay: {
    data: ContentData;
  };
  field_content_mandarin: {
    data: ContentData;
  };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Data {
  title: string;
  extroText: string;
  description: string[];
  introText: string;
  nextPage: {
    name: string;
    link: string;
  };
}

export interface AntiBriberyCorruption {
  id: number;
  title: string;
  field_content_english: {
    data: Data;
  };
  field_content_malay: {
    data: Data;
  };
  field_content_mandarin: {
    data: Data;
  };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Whistleblowing {
  id: number;
  title: string;
  field_content_english: {
    data: WhistleblowingData;
  };
  field_content_malay: {
    data: WhistleblowingData;
  };
  field_content_mandarin: {
    data: WhistleblowingData;
  };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WhistleblowingData {
  title: string;
  descriptions: string[];
  wicStructure: WicStructure;
  procedure: Procedure;
  nextPage: {
    name: string;
    link: string;
  };
}

export interface WicStructure {
  title: string;
  image: string;
}

export interface Procedure {
  title: string;
  image: string;
}

export interface WhistleblowingPolicy {
  id: number;
  title: string;
  field_content_english: {
    data: WhistleblowingPolicyData;
  };
  field_content_malay: {
    data: WhistleblowingPolicyData;
  };
  field_content_mandarin: {
    data: WhistleblowingPolicyData;
  };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface WhistleblowingPolicyData {
  markup: string;
  nextPage: {
    name: string;
    link: string;
  };
}

export interface ReportingConcern {
  id: number;
  title: string;
  field_content_english: {
    data: ReportingConcernData;
    labelData: LabelData;
  };
  field_content_malay: {
    data: ReportingConcernData;
    labelData: LabelData;
  };
  field_content_mandarin: {
    data: ReportingConcernData;
    labelData: LabelData;
  };
  field_page: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ReportingConcernData {
  title: string;
  description: string;
  afterSubmit: AfterSubmit;
  nextPage: {
    name: string;
    link: string;
  };
}

export interface AfterSubmit {
  succesMessage: string;
  appreciationMessage: string;
  backToForm: string;
}

export interface LabelData {
  persons: Persons;
  concerns: Concerns;
  yourParticulars: YourParticulars;
  confidentialityText: string;
  captcha: Captcha;
  submitButtonText: string;
}

export interface Persons {
  titleText: string;
  nameFieldText: string;
  designationFieldText: string;
  divisionOrUnitFieldText: string;
  companyOrAirportFieldText: string;
}

export interface Concerns {
  titleText: string;
  dateTimeFieldText: string;
  locationOfIncidentFieldText: string;
  detailsFieldText: string;
  otherPartiesInvolvedFieldText: string;
  namesOfWitnessesInvolvedFieldText: string;
}

export interface YourParticulars {
  titleText: string;
  fullnameFieldText: string;
  staffNoFieldText: string;
  contactNoFieldText: string;
  emailFieldText: string;
  companyNameFieldText: string;
}

export interface Captcha {
  titleText: string;
  helpText: string;
  newCaptchaText: string;
}

const useManagementIntegrity = (
  options?: UseQueryOptions<ManagementIntegrity[], DefaultError, ManagementIntegrity[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<ManagementIntegrity[], DefaultError> => {
  return useQuery<ManagementIntegrity[], DefaultError, ManagementIntegrity[], QueryKey>(
    {
      queryKey: ["managementIntegrityMessagePageContent"],
      enabled: false,
      queryFn: async (): Promise<ManagementIntegrity[]> => {
        const response = await corporateCMSApi.get<ManagementIntegrity[]>(
          `/api/page-content/all-fields?pageName=about-us/corporate-integrity/management-integrity-message`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useManagementIntegrityPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["managementIntegrityPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=about-us/corporate-integrity/management-integrity-message`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useAntiBriberyCorruption = (
  options?: UseQueryOptions<AntiBriberyCorruption[], DefaultError, AntiBriberyCorruption[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<AntiBriberyCorruption[], DefaultError> => {
  return useQuery<AntiBriberyCorruption[], DefaultError, AntiBriberyCorruption[], QueryKey>(
    {
      queryKey: ["antiBriberyCorruption"],
      enabled: false,
      queryFn: async (): Promise<AntiBriberyCorruption[]> => {
        const response = await corporateCMSApi.get<AntiBriberyCorruption[]>(
          `/api/page-content/all-fields?pageName=about-us/corporate-integrity/anti-bribery-and-corruption-policy-statement`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useAntiBriberyCorruptionPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["antiBriberyCorruptionPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=about-us/corporate-integrity/anti-bribery-and-corruption-policy-statement`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useWhistleblowing = (
  options?: UseQueryOptions<Whistleblowing[], DefaultError, Whistleblowing[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<Whistleblowing[], DefaultError> => {
  return useQuery<Whistleblowing[], DefaultError, Whistleblowing[], QueryKey>(
    {
      queryKey: ["whistleblowing"],
      enabled: false,
      queryFn: async (): Promise<Whistleblowing[]> => {
        const response = await corporateCMSApi.get<Whistleblowing[]>(
          `/api/page-content/all-fields?pageName=about-us/corporate-integrity/whistleblowing-program`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useWhistleblowingPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["whistleblowingPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=about-us/corporate-integrity/whistleblowing-program`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useWhistleblowingPolicyData = (
  options?: UseQueryOptions<WhistleblowingPolicy[], DefaultError, WhistleblowingPolicy[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<WhistleblowingPolicy[], DefaultError> => {
  return useQuery<WhistleblowingPolicy[], DefaultError, WhistleblowingPolicy[], QueryKey>(
    {
      queryKey: ["whistleblowingPolicyData"],
      enabled: false,
      queryFn: async (): Promise<WhistleblowingPolicy[]> => {
        const response = await corporateCMSApi.get<WhistleblowingPolicy[]>(
          `/api/page-content/all-fields?pageName=about-us/corporate-integrity/whistleblowing-policy`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useWhistleblowingPolicyPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["whistleblowingPolicyPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=about-us/corporate-integrity/whistleblowing-policy`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useReportingConcern = (
  options?: UseQueryOptions<ReportingConcern[], DefaultError, ReportingConcern[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<ReportingConcern[], DefaultError> => {
  return useQuery<ReportingConcern[], DefaultError, ReportingConcern[], QueryKey>(
    {
      queryKey: ["reportingConcern"],
      enabled: false,
      queryFn: async (): Promise<ReportingConcern[]> => {
        const response = await corporateCMSApi.get<ReportingConcern[]>(
          `/api/page-content/all-fields?pageName=about-us/corporate-integrity/reporting-concern-form`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

const useReportingConcernPageHeading = (
  options?: UseQueryOptions<PageHeading[], DefaultError, PageHeading[], QueryKey>,
  queryClient?: QueryClient
): UseQueryResult<PageHeading[], DefaultError> => {
  return useQuery<PageHeading[], DefaultError, PageHeading[], QueryKey>(
    {
      queryKey: ["reportingConcernPageHeading"],
      enabled: false,
      queryFn: async (): Promise<PageHeading[]> => {
        const response = await corporateCMSApi.get<PageHeading[]>(
          `/api/page-heading/all-fields?pageName=about-us/corporate-integrity/reporting-concern-form`
        );
        return response.data;
      },
      refetchOnWindowFocus: false,
      ...options,
    },
    queryClient
  );
};

export {
  useAntiBriberyCorruption,
  useAntiBriberyCorruptionPageHeading,
  useManagementIntegrity,
  useManagementIntegrityPageHeading,
  useReportingConcern,
  useReportingConcernPageHeading,
  useWhistleblowing,
  useWhistleblowingPageHeading,
  useWhistleblowingPolicyData,
  useWhistleblowingPolicyPageHeading,
};
